import React, { useState, useEffect } from 'react';

const ConversationSuggestions = ({ userProfile, matchProfile, environment, onBack, onEnvironmentChange }) => {
  const [suggestions, setSuggestions] = useState([]);
  const [urgencyLevel, setUrgencyLevel] = useState('medium');
  const [feedback, setFeedback] = useState({});
  const [copiedId, setCopiedId] = useState(null);

  // Conversation starters database
  const conversationDatabase = {
    intellectual: {
      low: [
        "What's the most interesting thing you've learned recently?",
        "Have you read any good books lately?",
        "What podcasts are you into these days?"
      ],
      medium: [
        "What's your take on [current event]?",
        "I've been thinking about [philosophical topic]. What's your perspective?",
        "Have you ever wondered why [interesting observation]?"
      ],
      high: [
        "Quick question - if you could solve one world problem, what would it be?",
        "Random thought - do you think AI will change how we form relationships?",
        "I need a different perspective - what defines success for you?"
      ]
    },
    humor_based: {
      low: [
        "What's the weirdest thing you've seen today?",
        "On a scale of 1-10, how much do you trust people who don't like pizza?",
        "What's your most useless talent?"
      ],
      medium: [
        "Okay, serious question - is a hot dog a sandwich?",
        "What's the most ridiculous thing you believed as a kid?",
        "If you had to live in a sitcom, which one would it be?"
      ],
      high: [
        "Emergency question - would you rather fight 100 duck-sized horses or one horse-sized duck?",
        "Quick - if your life was a movie, what would the title be?",
        "Plot twist - you can only communicate in movie quotes for the next hour. What's your opening line?"
      ]
    },
    storytelling: {
      low: [
        "What's the best thing that happened to you this week?",
        "Have you been on any adventures lately?",
        "What's your favorite memory from this year?"
      ],
      medium: [
        "So I just had the craziest thing happen... Have you ever [relatable experience]?",
        "You'll never believe what I saw on my way here...",
        "This reminds me of the time when... What about you?"
      ],
      high: [
        "Okay, I need your take on this wild situation...",
        "So picture this scenario...",
        "I have to tell you about what just happened..."
      ]
    },
    direct: {
      low: [
        "How's your night going?",
        "What brings you here?",
        "Are you from around here?"
      ],
      medium: [
        "What do you do for fun?",
        "How do you know [mutual connection/host]?",
        "What are you drinking?"
      ],
      high: [
        "Mind if I join you?",
        "You seem interesting. What's your story?",
        "I don't usually do this, but I wanted to say hi."
      ]
    },
    casual: {
      low: [
        "Hey, how's it going?",
        "This place is nice, right?",
        "Have you been here before?"
      ],
      medium: [
        "What do you think of the music?",
        "Any recommendations for [drinks/food]?",
        "How do you know people here?"
      ],
      high: [
        "Is it just me or is it [observation about environment]?",
        "I'm terrible at small talk. What should we actually talk about?",
        "What's the most interesting thing about you that I wouldn't guess?"
      ]
    }
  };

  useEffect(() => {
    generateSuggestions();
  }, [userProfile, matchProfile, environment, urgencyLevel]);

  const generateSuggestions = () => {
    const style = userProfile?.personality?.communicationStyle || 'casual';
    const starters = conversationDatabase[style]?.[urgencyLevel] || conversationDatabase.casual[urgencyLevel];
    
    // Add environment-specific modifications
    const modifiedStarters = starters.map(starter => {
      if (environment.venueType === 'coffee_shop' && environment.timeOfDay === 'morning') {
        return starter.replace('[drinks/food]', 'coffee');
      } else if (environment.venueType === 'upscale_cocktail_bar') {
        return starter.replace('[drinks/food]', 'cocktails here');
      }
      return starter;
    });

    // Calculate confidence scores based on personality match
    const suggestionsWithScores = modifiedStarters.map((text, index) => ({
      id: `sug_${Date.now()}_${index}`,
      text,
      confidence: calculateConfidence(text),
      category: getCategoryForStarter(text),
      source: 'personality_match'
    }));

    setSuggestions(suggestionsWithScores);
  };

  const calculateConfidence = (starter) => {
    // Base confidence on personality compatibility
    const baseConfidence = matchProfile?.compatibility?.overall || 0.7;
    
    // Adjust based on environment
    let environmentModifier = 1;
    if (environment.noiseLevel === 'high' && starter.length > 50) {
      environmentModifier = 0.8; // Longer starters less effective in noisy environments
    }
    if (environment.crowdEnergy === 'high' && urgencyLevel === 'low') {
      environmentModifier = 0.9; // Low urgency might not work in high energy
    }
    
    return Math.min(0.95, baseConfidence * environmentModifier);
  };

  const getCategoryForStarter = (text) => {
    if (text.includes('?')) {
      if (text.length < 30) return 'question';
      return 'deep_question';
    }
    if (text.includes('...')) return 'story_prompt';
    return 'statement';
  };

  const handleFeedback = (suggestionId, type) => {
    setFeedback(prev => ({
      ...prev,
      [suggestionId]: type
    }));
    
    // In a real app, this would send feedback to improve suggestions
    console.log(`Feedback recorded: ${type} for suggestion ${suggestionId}`);
  };

  const copyToClipboard = (text, id) => {
    navigator.clipboard.writeText(text);
    setCopiedId(id);
    setTimeout(() => setCopiedId(null), 2000);
  };

  const venueOptions = [
    { value: 'upscale_cocktail_bar', label: '🍸 Upscale Bar' },
    { value: 'dive_bar', label: '🍺 Dive Bar' },
    { value: 'coffee_shop', label: '☕ Coffee Shop' },
    { value: 'rooftop_lounge', label: '🌃 Rooftop Lounge' },
    { value: 'sports_bar', label: '🏈 Sports Bar' },
    { value: 'wine_bar', label: '🍷 Wine Bar' }
  ];

  return (
    <div className="conversation-suggestions">
      <div className="mb-6">
        <button
          onClick={onBack}
          className="text-gray-400 hover:text-white mb-4"
        >
          ← Back to Matches
        </button>
        
        <h2 className="text-2xl font-bold text-white mb-2">
          Conversation Starters for {matchProfile.name}
        </h2>
        <p className="text-gray-400">
          Personalized suggestions based on your {userProfile?.personality?.communicationStyle || 'casual'} communication style
        </p>
      </div>

      {/* Environment Settings */}
      <div className="bg-gray-800 rounded-lg p-4 mb-6">
        <h3 className="text-lg font-semibold text-white mb-3">Environment Settings</h3>
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="block text-sm text-gray-400 mb-1">Venue Type</label>
            <select
              value={environment.venueType}
              onChange={(e) => onEnvironmentChange({ ...environment, venueType: e.target.value })}
              className="w-full px-3 py-2 bg-gray-700 text-white rounded-lg"
            >
              {venueOptions.map(opt => (
                <option key={opt.value} value={opt.value}>{opt.label}</option>
              ))}
            </select>
          </div>
          
          <div>
            <label className="block text-sm text-gray-400 mb-1">Urgency Level</label>
            <select
              value={urgencyLevel}
              onChange={(e) => setUrgencyLevel(e.target.value)}
              className="w-full px-3 py-2 bg-gray-700 text-white rounded-lg"
            >
              <option value="low">Low - Casual Opener</option>
              <option value="medium">Medium - Engaging</option>
              <option value="high">High - Attention Grabbing</option>
            </select>
          </div>
        </div>
      </div>

      {/* Suggestions */}
      <div className="space-y-4">
        {suggestions.map((suggestion) => (
          <div key={suggestion.id} className="bg-gray-800 rounded-lg p-4">
            <div className="flex justify-between items-start mb-3">
              <p className="text-white text-lg flex-1 mr-4">
                "{suggestion.text}"
              </p>
              <span className="px-2 py-1 bg-blue-900 text-blue-300 rounded-full text-xs">
                {Math.round(suggestion.confidence * 100)}%
              </span>
            </div>
            
            <div className="flex items-center justify-between">
              <span className="text-xs text-gray-400">
                {suggestion.category} • {suggestion.source}
              </span>
              
              <div className="flex gap-2">
                <button
                  onClick={() => copyToClipboard(suggestion.text, suggestion.id)}
                  className="px-3 py-1 text-sm bg-gray-700 text-gray-300 rounded hover:bg-gray-600"
                >
                  {copiedId === suggestion.id ? '✓ Copied' : '📋 Copy'}
                </button>
                
                <button
                  onClick={() => handleFeedback(suggestion.id, 'success')}
                  className={`px-3 py-1 text-sm rounded ${
                    feedback[suggestion.id] === 'success'
                      ? 'bg-green-800 text-green-300'
                      : 'bg-gray-700 text-gray-300 hover:bg-gray-600'
                  }`}
                >
                  👍 Worked
                </button>
                
                <button
                  onClick={() => handleFeedback(suggestion.id, 'failed')}
                  className={`px-3 py-1 text-sm rounded ${
                    feedback[suggestion.id] === 'failed'
                      ? 'bg-red-800 text-red-300'
                      : 'bg-gray-700 text-gray-300 hover:bg-gray-600'
                  }`}
                >
                  👎 Didn't Work
                </button>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Refresh Button */}
      <div className="mt-6 text-center">
        <button
          onClick={generateSuggestions}
          className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
        >
          🔄 Generate New Suggestions
        </button>
      </div>
    </div>
  );
};

export default ConversationSuggestions;